import { useState } from "react";
import { useNavigate } from "react-router";

import { useAppSelector } from "@/app/hooks";
import { selectWorkspaceStoreSeedBindingId } from "@/features/settings/activeStoreSlice";
import { useGetMerchantStoreBindingsQuery } from "@/features/settings/storeManagementApi";
import { MerryButton } from "@/shared/ui/MerryButton";
import { conversationUnassignedShopNoticeKind } from "./unassignedShopNotice";
import { useCreateWorkspaceConversation } from "./useCreateWorkspaceConversation";
import { workspaceConversationPath } from "./conversationShopScope";

const emptySendContext = { resourceIds: [] as const, resourceRefs: [] as const };

export function ConversationUnassignedShopNotice({
  canSendAI,
  conversationShopBindingId,
  organizationId,
}: {
  canSendAI: boolean;
  conversationShopBindingId?: string | null;
  organizationId: string;
}) {
  const navigate = useNavigate();
  const seedId = useAppSelector((state) =>
    selectWorkspaceStoreSeedBindingId(state, organizationId),
  );
  const stores = useGetMerchantStoreBindingsQuery(organizationId, {
    skip: !organizationId,
  });
  const seed = stores.currentData?.bindings.find(
    (binding) => binding.id === seedId && binding.status === "active",
  );
  const create = useCreateWorkspaceConversation(canSendAI, Boolean(seed));
  const [error, setError] = useState("");
  const kind = conversationUnassignedShopNoticeKind({
    conversationShopBindingId,
    seedDisplayName: seed?.displayName,
  });
  if (kind === "hidden") return null;
  if (kind !== "create" || !seed) {
    return (
      <aside className="merry-conversation__shop-scope-notice" role="status">
        <p>
          当前对话没有冻结店铺，不能后绑或从店铺清单里挑选。请先在顶部选择工作区店铺，再开新对话。
        </p>
      </aside>
    );
  }
  return (
    <aside className="merry-conversation__shop-scope-notice" role="status">
      <p>
        当前对话没有冻结店铺，不能按工作区店铺继续。请用「{seed.displayName}」开新对话。
      </p>
      <MerryButton
        hierarchy="level2"
        isDisabled={create.isCreating || !create.ready}
        onPress={() => {
          setError("");
          void create
            .launch("新对话", emptySendContext, {
              preferredShopBindingId: seed.id,
            })
            .then((result) => {
              void navigate(
                workspaceConversationPath(result.conversationId, result.shopBindingId),
                {
                  replace: true,
                  state: {
                    conversationId: result.conversationId,
                    shopBindingId: result.shopBindingId,
                  },
                },
              );
            })
            .catch((reason: unknown) => {
              setError(
                reason instanceof Error
                  ? reason.message
                  : "新对话创建失败，请重试。",
              );
            });
        }}
      >
        {create.isCreating ? "正在创建…" : "用当前店铺开新对话"}
      </MerryButton>
      {error ? <p role="alert">{error}</p> : null}
    </aside>
  );
}
